import { Location as NgLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import type {
  NavigationExtras,
  UrlCreationOptions,
  UrlTree,
} from '@angular/router';
import { Router } from '@angular/router';
import { TimeUtils } from '@freelancer/time-utils';
import type { Interface } from '@freelancer/types';
import { isEqual } from '@freelancer/utils';
import type { Observable, SubscriptionLike } from 'rxjs';
import { ReplaySubject } from 'rxjs';
import type { Location, LocationEvent } from '../location.service';

/**
 * Differences with the real Location service:
 * - `back` method backs after the subscription rather than before
 * - `externalRedirectUrl` to keep track of navigations outside of the webapp, e.g.
 *   RedirectToPhp service, window.location redirects
 *
 * NOTE: Keep this in sync with the Location service
 */
@Injectable({
  providedIn: 'root',
})
export class LocationTesting implements Interface<Location> {
  private routeLoaded = false;
  private locationSubject$ = new ReplaySubject<LocationEvent>(1);

  externalRedirectUrl: string | undefined;

  constructor(
    private router: Router,
    private location: NgLocation,
    private timeUtils: TimeUtils,
  ) {}

  /**
   * Emits a stream of LocationEvent
   */
  valueChanges(): Observable<LocationEvent> {
    return this.locationSubject$.asObservable();
  }

  /**
   * Pushes a fake state to absorb a back button click.
   * Returns a subscription for calling `onPop` after the state is popped.
   *
   * PRIVATE: this is for modals/callouts and should not be needed in app code.
   * Discuss with Frontend Infrastructure if you need back button interactions.
   */
  _createBackButtonState({ onPop }: { onPop(): void }): SubscriptionLike {
    // keep track of the initial state
    const prevState = this.location.getState();
    // non-navigation that just pushes a state for back button compatibility
    this.location.go(this.location.path(), undefined, { fakeId: Date.now() });
    // return a subscription so it can be unsubscribed
    const sub = this.location.subscribe(({ state }) => {
      // if we're back at the original state, call the onPop
      if (isEqual(state, prevState)) {
        if (onPop) {
          onPop();
        }
        // unsubscribe after first pop event
        sub.unsubscribe();
      }
    });
    return sub;
  }

  /**
   * Navigates to the previous state in the route history,
   * returning a Promise that resolves when the navigation is complete
   */
  back(): Promise<void> {
    if (this.routeLoaded) {
      return new Promise(resolve => {
        // wait for the next `popstate event to be finished
        // otherwise if you try to back().then() navigate, it will be cancelled
        const sub = this.location.subscribe(() => {
          setTimeout(() => resolve());
          sub.unsubscribe();
        });
        // TODO: T170437 don't back if there's no previous state in the SPA
        this.location.back();

        // FIX ME: T290085 - Investigate why location.go pushes two states and
        // location.back doesn't properly clean top state in history

        // There are cases that location.back() doesn't trigger a popstate event
        // which results in the location.subscribe always listens to new popstate
        // event and never resolves the promise. We added a setTimeout for 100ms
        // to wait for a navigation to finish then resolve the promise and unsubscribes
        // the subscription. This is a workaround to resolve when location.back()
        // doesn't trigger a popstate event.

        this.timeUtils.setTimeout(() => {
          if (!sub.closed) {
            resolve();
            sub.unsubscribe();
          }
        }, 100);
      });
    }
    // not `routeLoaded` implies compat layer,
    // where we don't track locationSubject$ and can't do the wait logic.
    return new Promise(resolve => {
      setTimeout(() => resolve());
    });
  }

  /**
   * SSR-friendly version of Router::navigateByUrl() without query parameters
   *
   * @remark
   *
   * WARNING: Please prefer using `router.navigate` if you need to pass in query
   * params to the router
   */
  navigateByUrl(
    url: string | UrlTree,
    extras?: NavigationExtras,
  ): Promise<boolean> {
    return this.router.navigateByUrl(url, extras);
  }

  createUrlTree(
    commands: any[],
    navigationExtras?: UrlCreationOptions & {
      isPermanentRedirect?: boolean;
    },
  ): UrlTree {
    return this.router.createUrlTree(commands, navigationExtras);
  }

  /**
   * Sets the location to a provided url. This will leave the webapp, DO NOT
   * use it for internal redirects, use navigateByUrl instead
   *
   * NOTE: This is a no-op in tests to prevent making HTTP requests to external
   * pages (which won't work anyway).
   */
  async redirect(url: string): Promise<boolean> {
    this.setExternalRedirectUrl(url);
    await new Promise(resolve => {
      // Never return
    });
    return false;
  }

  /**
   * Used for marking what history states are the bottom of the history stack.
   */
  markInitialState(): void {
    this.routeLoaded = true;
  }

  /*
   * Returns the current origin in a platform friendly way
   */
  get origin(): string {
    return window.location.origin;
  }

  /*
   * Returns the current host, that is the hostname, a ':', and the port of the
   * URL, in a platform friendly way
   */
  get host(): string {
    return window.location.host;
  }

  /*
   * Returns the current hostname, that is the domain of the URL, in a platform
   * friendly way
   */
  get hostname(): string {
    return window.location.hostname;
  }

  /*
   * Returns the current (absolute) URL in a platform friendly way
   */
  get href(): string {
    return `${window.location.origin}${this.router.url}`;
  }

  get searchParams(): URLSearchParams {
    const url = new URL(this.href);
    return url.searchParams;
  }

  /*
   * Returns the current URL in a platform friendly way
   */
  get url(): string {
    return this.router.url;
  }

  /**
   * Returns the current pathname in a platform friendly way
   */
  get pathname(): string {
    return window.location.pathname;
  }

  /**
   * Returns the current protocol in a platform friendly way
   */
  get protocol(): string {
    return window.location.protocol;
  }

  setExternalRedirectUrl(url: string | undefined): void {
    this.externalRedirectUrl = url;
  }

  /**
   * [PRIVATE] Exported private API.
   * To be used only by the LocationComponent to push new locations
   */
  _setLocation(pathname: string, hash: string, search: string): void {
    this.locationSubject$.next({
      hash,
      hostname: this.hostname,
      href: `${this.origin}${pathname}`,
      origin: this.origin,
      pathname,
      search,
    });
  }
}
